import { CrmTaskStatus } from '@plato/data/prisma';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { formatMonthShortDay } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import {
    Card,
    CardComplete,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardMainAction,
    CardOpenIcon,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { cn } from '@plato/ui/util/classes';
import { UserIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmTaskInputDTO } from '../../../server/dto/crm-task-input-dto.ts';
import type { TaskFormSuggestion } from '../../tasks/components/task-form-dialog.tsx';
import { TaskFormDialog } from '../../tasks/components/task-form-dialog.tsx';
import { deriveTaskText } from '../../tasks/lib/derive-task-text.ts';
import type { TaskListItem } from '../log-interaction-dialog-context.ts';

type TaskDialogProps = {
    title: string;
    initialValues: Partial<CrmTaskInputDTO>;
    suggestions?: TaskFormSuggestion[];
    onSubmit: (task: CrmTaskInputDTO) => void | Promise<void>;
};

/** The notes-side task form dialog — the create flow opens it with its own title. */
export const TaskDialog = DialogStateManager.create<TaskDialogProps>(
    ({ title, initialValues, suggestions, onSubmit }: TaskDialogProps): ReactNode => {
        const { visible, remove } = useDialog();

        return (
            <TaskFormDialog
                open={visible}
                onClose={remove}
                onSubmit={onSubmit}
                title={title}
                initialValues={initialValues}
                suggestions={suggestions}
            />
        );
    },
);

export type LogInteractionTaskCardProps = {
    item: TaskListItem;
};

/**
 * A task staged in this log, in the Aufgaben section or joined under the offer it
 * links to. Both read the same form state, so a task looks and edits the same way
 * wherever it appears.
 */
export function LogInteractionTaskCard({ item }: LogInteractionTaskCardProps): ReactNode {
    const { t } = useTranslation('notes');
    const locale = useResolvedLocale();

    const { title, detail } = deriveTaskText(item.values.description);
    const dueLabel = formatMonthShortDay(item.values.dueDate, locale);
    const completed = item.values.status === CrmTaskStatus.COMPLETED;

    return (
        <Card className={item.pending ? 'border-dashed' : undefined}>
            <CardFront className="card-completed:no-underline!">
                <CardComplete
                    variant="primary"
                    checked={completed}
                    onChange={(event) => {
                        item.onChange({
                            ...item.values,
                            status: event.target.checked ? CrmTaskStatus.COMPLETED : CrmTaskStatus.OPEN,
                        });
                    }}
                />
                <CardContent>
                    <CardHeader className="flex-nowrap items-start @md:items-start">
                        <CardTitle className={cn('line-clamp-2 min-w-0 flex-1', completed && 'line-through')}>
                            {title}
                        </CardTitle>
                        <CardSubtitle className="shrink-0 whitespace-nowrap">
                            {item.assigneeName && (
                                <>
                                    <UserIcon className="size-4" /> {item.assigneeName}
                                </>
                            )}
                            {dueLabel && (
                                <>
                                    {item.assigneeName && ' · '}
                                    {dueLabel}
                                </>
                            )}
                        </CardSubtitle>
                    </CardHeader>
                    {detail && !completed && (
                        <CardDetail className="line-clamp-3 text-content-weak">{detail}</CardDetail>
                    )}
                </CardContent>
                <CardMainAction
                    onClick={() => {
                        void DialogStateManager.show(TaskDialog, {
                            title: t('tasks__form_edit_title', { ns: 'tasks' }),
                            initialValues: item.values,
                            onSubmit: item.onChange,
                        });
                    }}
                >
                    <CardOpenIcon />
                </CardMainAction>
            </CardFront>
        </Card>
    );
}
